import React, { useEffect, useState } from "react";
import {
  getLoginUrl,
  startBot,
  stopBot,
  runUpstoxAutoLogin,
  logoutServer,
  postDashboardLogout,
  addWatchlistSymbol,
  removeWatchlistSymbol,
  createWatchlist,
  setActiveWatchlist,
  deleteWatchlist,
  getInstrumentSearch,
  sendTestNotification,
  formatApiError,
} from "../api";
import useMediaQuery from "../hooks/useMediaQuery";
import { downloadCsv } from "../utils/csv";
import { formatIstDateTime, formatIstTime } from "../utils/time";

const clearBacktestSessionResult = () => {
  try { window.sessionStorage.removeItem("algobot:backtest:latest-result"); } catch (_) {}
};

export default function Settings({ botStatus, onRefresh, dashboard }) {
  const isMobile = useMediaQuery("(max-width: 760px)");
  const [connecting, setConnecting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [watchSymbol, setWatchSymbol] = useState("");
  const [watchInstrumentKey, setWatchInstrumentKey] = useState("");
  const [watchSuggestions, setWatchSuggestions] = useState([]);
  const [watchBusy, setWatchBusy] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [autoLoginBusy, setAutoLoginBusy] = useState(false);
  const [autoLoginResult, setAutoLoginResult] = useState(null);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);

  useEffect(() => {
    const q = watchSymbol.trim().toUpperCase();
    if (q.length < 1) {
      setWatchSuggestions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const data = await getInstrumentSearch(q);
        setWatchSuggestions(data?.matches || []);
      } catch {
        setWatchSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [watchSymbol]);

  const handleConnect = async () => {
    setConnecting(true);
    setMsg(null);
    try {
      const { url, note } = await getLoginUrl();
      window.open(url, "_blank", "width=600,height=700");
      setMsg({ type: "info", text: note || "Upstox login page opened. Complete the login then come back here." });
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err?.raw || err) });
    }
    setConnecting(false);
  };

  const handleLogout = async () => {
    setMsg(null);
    try {
      await logoutServer();
      clearBacktestSessionResult();
      setMsg({ type: "success", text: "Saved token removed on server. Connect again when needed." });
      await onRefresh();
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err?.raw || err) });
    }
  };

  const handleBotToggle = async () => {
    setMsg(null);
    try {
      const wasActive = botStatus?.botActive;
      
      if (wasActive) { 
        setMsg({ type: "info", text: "Stopping bot..." });
        await stopBot(); 
      }
      else { 
        setMsg({ type: "info", text: "Starting bot..." });
        await startBot(); 
      }
      
      // Wait and refresh status
      await new Promise(r => setTimeout(r, 800));
      await onRefresh();
      
      setMsg({ 
        type: "success", 
        text: wasActive ? "Bot stopped successfully" : "Bot started successfully" 
      });
      setTimeout(() => setMsg(null), 2000);
    } catch (err) { 
      const errMsg = err?.raw ? formatApiError(err.raw) : err.message || "Unknown error";
      console.error("Bot toggle error:", errMsg);
      setMsg({ 
        type: "error", 
        text: `Failed to toggle bot: ${errMsg}` 
      });
      // Try refresh anyway
      setTimeout(() => {
        try { onRefresh(); } catch (_) {}
      }, 1000);
    }
  };

  const handleManualAutoLogin = async () => {
    setMsg(null);
    setAutoLoginBusy(true);
    setAutoLoginResult(null);
    try {
      const result = await runUpstoxAutoLogin();
      setAutoLoginResult(result);
      setMsg({
        type: result?.status === "ok" ? "success" : "error",
        text: result?.status === "ok" ? "Upstox auto-login completed successfully." : (result?.error || "Upstox auto-login failed."),
      });
      await onRefresh();
    } catch (err) {
      const backendResult = err?.raw?.response?.data;
      setAutoLoginResult(backendResult || { status: "error", error: formatApiError(err?.raw || err) });
      setMsg({ type: "error", text: backendResult?.error || formatApiError(err?.raw || err) });
      setTimeout(() => {
        try { onRefresh(); } catch (_) {}
      }, 1000);
    } finally {
      setAutoLoginBusy(false);
    }
  };

  const handleTestNotification = async () => {
    setMsg(null);
    setNotificationBusy(true);
    setNotificationResult(null);
    try {
      const result = await sendTestNotification();
      setNotificationResult(result);
      setMsg({ type: "success", text: "Telegram test notification sent." });
      await onRefresh();
    } catch (err) {
      const backendResult = err?.raw?.response?.data;
      setNotificationResult(backendResult || { ok: false, error: formatApiError(err?.raw || err) });
      setMsg({ type: "error", text: backendResult?.error || formatApiError(err?.raw || err) });
    } finally {
      setNotificationBusy(false);
    }
  };

  const handleAddWatchlist = async (e) => {
    e.preventDefault();
    const symbol = watchSymbol.trim().toUpperCase();
    if (!symbol) return;
    setWatchBusy(true);
    setMsg(null);
    try {
      await addWatchlistSymbol({ symbol, instrumentKey: watchInstrumentKey || undefined, listName: botStatus?.watchlists?.active });
      setWatchSymbol("");
      setWatchInstrumentKey("");
      setWatchSuggestions([]);
      setMsg({ type: "success", text: `${symbol} added to manual watchlist.` });
      await onRefresh();
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err?.raw || err) });
    }
    setWatchBusy(false);
  };

  const handleCreateWatchlist = async (e) => {
    e.preventDefault();
    const name = newWatchlistName.trim();
    if (!name) return;
    setWatchBusy(true);
    setMsg(null);
    try {
      await createWatchlist(name);
      setNewWatchlistName("");
      setMsg({ type: "success", text: `Watchlist "${name}" created.` });
      await onRefresh();
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err?.raw || err) });
    }
    setWatchBusy(false);
  };

  const handleActiveWatchlist = async (name) => {
    setWatchBusy(true);
    setMsg(null);
    try {
      await setActiveWatchlist(name);
      setMsg({ type: "success", text: `Using watchlist "${name}".` });
      await onRefresh();
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err?.raw || err) });
    }
    setWatchBusy(false);
  };

  const handleDeleteWatchlist = async (name) => {
    setWatchBusy(true);
    setMsg(null);
    try {
      await deleteWatchlist(name);
      setMsg({ type: "success", text: `Watchlist "${name}" deleted.` });
      await onRefresh();
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err?.raw || err) });
    }
    setWatchBusy(false);
  };

  const handleRemoveWatchlist = async (symbol) => {
    setWatchBusy(true);
    setMsg(null);
    try {
      await removeWatchlistSymbol(symbol, botStatus?.watchlists?.active);
      setMsg({ type: "success", text: `${symbol} removed from manual watchlist.` });
      await onRefresh();
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err?.raw || err) });
    }
    setWatchBusy(false);
  };

  const Row = ({ label, value, valueColor }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #0a1628", fontSize: 12 }}>
      <span style={{ color: "#64748b" }}>{label}</span>
      <span style={{ color: valueColor || "#e2e8f0", fontWeight: 600 }}>{value}</span>
    </div>
  );
  const formatConfigValue = (value) => {
    if (value == null || value === "") return "—";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };
  const configGroups = [
    ["Server", botStatus?.envConfig?.server],
    ["Auth Toggles", botStatus?.envConfig?.auth],
    ["Trading Risk", botStatus?.envConfig?.trading],
    ["Strategies", botStatus?.envConfig?.strategies],
    ["Scanner", botStatus?.envConfig?.scanner],
    ["Auto Scanner", botStatus?.envConfig?.autoScanner],
    ["Notifications", botStatus?.envConfig?.notifications],
    ["Universe", botStatus?.envConfig?.universe],
    ["Costs", botStatus?.envConfig?.costs],
  ].filter(([, values]) => values && Object.keys(values).length > 0);
  const envConfigRows = configGroups.flatMap(([section, values]) =>
    Object.entries(values || {}).map(([key, value]) => ({
      section,
      key,
      value: formatConfigValue(value),
    }))
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,minmax(0,1fr))", gap: 14, maxWidth: isMobile ? "100%" : 980 }}>
      {/* Upstox Connection */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Syne',sans-serif", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #0f172a" }}>
          🔗 Upstox API Connection
        </div>
        <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          <strong style={{ color: "#94a3b8" }}>Free API steps:</strong><br />
          1. Go to <span style={{ color: "#38bdf8" }}>upstox.com/developer</span><br />
          2. Create an app → set Redirect URI to your public <code style={{ color: "#f59e0b", fontSize: 10 }}>/callback</code> URL on this server (same as <code style={{ color: "#f59e0b", fontSize: 10 }}>REDIRECT_URI</code> in <code style={{ color: "#f59e0b", fontSize: 10 }}>.env</code>)<br />
          3. Copy API Key + Secret into backend <code style={{ color: "#f59e0b", fontSize: 10 }}>.env</code><br />
          4. Click Connect below
        </div>
        <div style={{ fontSize: 11, color: "#f59e0b", lineHeight: 1.6, marginBottom: 16, background: "#1f1200", border: "1px solid #f59e0b33", borderRadius: 8, padding: 10 }}>
          Server auto-login, if enabled, runs only on the backend using private PIN/TOTP variables. The browser never receives those values.
        </div>
        <button onClick={handleConnect} disabled={connecting} style={{
          width: "100%", background: "linear-gradient(135deg,#0ea5e9,#0284c7)",
          border: "none", color: "#fff", padding: "12px",
          borderRadius: 8, cursor: "pointer", fontFamily: "'DM Mono',monospace",
          fontSize: 12, fontWeight: 600, marginBottom: 10,
        }}>
          {connecting ? "Opening..." : "↗ Connect to Upstox"}
        </button>
        {msg && (
          <div style={{ fontSize: 11, color: msg.type === "error" ? "#f87171" : "#60a5fa", lineHeight: 1.6, marginTop: 8 }}>
            {msg.text}
          </div>
        )}
      </div>

      {/* Bot Status */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Syne',sans-serif", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #0f172a" }}>
          🤖 Bot Status
        </div>
        {botStatus ? (
          <>
            <Row label="Bot Active"      value={botStatus.botActive ? "✅ Running" : "⏹ Stopped"} valueColor={botStatus.botActive ? "#22c55e" : "#ef4444"} />
            <Row label="Authenticated"   value={botStatus.authenticated ? "Yes" : "No"} valueColor={botStatus.authenticated ? "#22c55e" : "#f59e0b"} />
            <Row label="Paper Trading"   value={botStatus.paperTrading ? "ON (Safe)" : "OFF — LIVE ⚠️"} valueColor={botStatus.paperTrading ? "#f59e0b" : "#ef4444"} />
            <Row label="Market Status"   value={botStatus.marketOpen ? "🟢 Open" : "🔴 Closed"} />
            <Row label="Server IST" value={`${botStatus.marketClock?.istDate || "—"} ${botStatus.marketClock?.istTime || ""}`} valueColor="#38bdf8" />
            <Row label="WS Connected"    value={botStatus.ws?.connected ? "✅ Yes" : "❌ No"} valueColor={botStatus.ws?.connected ? "#22c55e" : "#ef4444"} />
            <Row label="Instrument list" value={botStatus.instrumentSource === "upstox_bod" ? "Upstox BOD (market)" : ".env static"} valueColor="#38bdf8" />
            <Row label="Instruments"     value={`${botStatus.ws?.instruments || botStatus.instruments || 0} subscribed`} />
            <Row label="Open Positions"  value={`${botStatus.openPositions}/${botStatus.maxPositions}`} />
            <Row label="BOT_AUTO_START" value={botStatus.botAutoStart ? "true (reconnect after restart)" : "false"} valueColor={botStatus.botAutoStart ? "#22c55e" : "#64748b"} />
            <Row
              label="Upstox auto-login"
              value={botStatus.upstoxAutoLogin?.enabled ? `ON (${botStatus.upstoxAutoLogin.time || "08:55"} IST)` : "OFF"}
              valueColor={botStatus.upstoxAutoLogin?.enabled ? "#22c55e" : "#64748b"}
            />
            <Row
              label="Last auto-login"
              value={botStatus.upstoxAutoLogin?.lastRun?.finishedAt ? `${formatIstTime(botStatus.upstoxAutoLogin.lastRun.finishedAt)} IST · ${botStatus.upstoxAutoLogin.lastRun.status}` : "—"}
              valueColor={botStatus.upstoxAutoLogin?.lastRun?.status === "error" ? "#ef4444" : "#64748b"}
            />
            <button
              type="button"
              onClick={handleManualAutoLogin}
              disabled={autoLoginBusy || !botStatus.upstoxAutoLogin?.enabled}
              style={{
                width: "100%",
                marginTop: 12,
                background: botStatus.upstoxAutoLogin?.enabled ? "#10243a" : "#0f172a",
                border: `1px solid ${botStatus.upstoxAutoLogin?.enabled ? "#38bdf8" : "#334155"}`,
                color: botStatus.upstoxAutoLogin?.enabled ? "#38bdf8" : "#64748b",
                padding: "10px",
                borderRadius: 8,
                cursor: autoLoginBusy || !botStatus.upstoxAutoLogin?.enabled ? "not-allowed" : "pointer",
                fontFamily: "'DM Mono',monospace",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {autoLoginBusy ? "Running server auto-login..." : "Run Upstox auto-login now"}
            </button>
            {!botStatus.upstoxAutoLogin?.enabled && (
              <div style={{ marginTop: 8, fontSize: 10, color: "#64748b", lineHeight: 1.5 }}>
                Enable <code style={{ color: "#f59e0b" }}>UPSTOX_AUTO_LOGIN_ENABLED=true</code> on the backend to use manual server auto-login.
              </div>
            )}
            {(autoLoginResult || botStatus.upstoxAutoLogin?.lastRun?.status === "error") && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  borderRadius: 8,
                  border: `1px solid ${(autoLoginResult?.status || botStatus.upstoxAutoLogin?.lastRun?.status) === "ok" ? "#14532d" : "#7f1d1d"}`,
                  background: (autoLoginResult?.status || botStatus.upstoxAutoLogin?.lastRun?.status) === "ok" ? "#052e16" : "#1f0a0a",
                  color: "#e2e8f0",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  lineHeight: 1.6,
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                }}
              >
                {(() => {
                  const result = autoLoginResult || botStatus.upstoxAutoLogin?.lastRun || {};
                  return [
                    `status: ${result.status || "unknown"}`,
                    result.reason ? `reason: ${result.reason}` : null,
                    result.startedAt ? `started: ${formatIstDateTime(result.startedAt)} IST` : null,
                    result.finishedAt ? `finished: ${formatIstDateTime(result.finishedAt)} IST` : null,
                    result.error ? `error: ${result.error}` : null,
                    result.stderr ? `stderr: ${result.stderr}` : null,
                  ].filter(Boolean).join("\n");
                })()}
              </div>
            )}
            <Row
              label="Telegram alerts"
              value={botStatus.notifications?.telegramConfigured ? "ON" : (botStatus.notifications?.enabled ? "Needs setup" : "OFF")}
              valueColor={botStatus.notifications?.telegramConfigured ? "#22c55e" : (botStatus.notifications?.enabled ? "#f59e0b" : "#64748b")}
            />
            <Row
              label="Telegram config"
              value={`token ${botStatus.notifications?.telegramTokenConfigured ? "yes" : "no"} / chat ${botStatus.notifications?.telegramChatConfigured ? "yes" : "no"}`}
              valueColor={botStatus.notifications?.telegramConfigured ? "#38bdf8" : "#f59e0b"}
            />
            <Row
              label="Telegram events"
              value={[
                botStatus.notifications?.events?.entry ? "entry" : null,
                botStatus.notifications?.events?.exit ? "exit" : null,
                botStatus.notifications?.events?.stopChange ? "stop/trailing" : null,
                botStatus.notifications?.events?.aiGate ? "ai-approved" : null,
              ].filter(Boolean).join(", ") || "none"}
            />
            <button
              type="button"
              onClick={handleTestNotification}
              disabled={notificationBusy || !botStatus.notifications?.enabled}
              style={{
                width: "100%",
                marginTop: 12,
                background: botStatus.notifications?.enabled ? "#10243a" : "#0f172a",
                border: `1px solid ${botStatus.notifications?.enabled ? "#38bdf8" : "#334155"}`,
                color: botStatus.notifications?.enabled ? "#38bdf8" : "#64748b",
                padding: "10px",
                borderRadius: 8,
                cursor: notificationBusy || !botStatus.notifications?.enabled ? "not-allowed" : "pointer",
                fontFamily: "'DM Mono',monospace",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {notificationBusy ? "Sending Telegram test..." : "Send Telegram test"}
            </button>
            {notificationResult && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  borderRadius: 8,
                  border: `1px solid ${notificationResult.ok === false ? "#7f1d1d" : "#14532d"}`,
                  background: notificationResult.ok === false ? "#1f0a0a" : "#052e16",
                  color: "#e2e8f0",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  lineHeight: 1.6,
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                }}
              >
                {notificationResult.ok === false
                  ? `error: ${notificationResult.error || "Telegram notification test failed."}`
                  : "status: sent"}
              </div>
            )}
            <Row label="Auto Scanner" value={botStatus.autoTradeFromScanner ? `ON (${Math.round((botStatus.autoScannerIntervalMs || 0) / 60000)} min)` : "OFF"} valueColor={botStatus.autoTradeFromScanner ? "#22c55e" : "#64748b"} />
            <Row
              label="Last auto scan"
              value={
                botStatus.lastAutoScannerRun?.finishedAt
                  ? `${formatIstTime(botStatus.lastAutoScannerRun.finishedAt) + " IST"} · ${(botStatus.lastAutoScannerRun.entries || []).length} entries`
                  : "—"
              }
              valueColor={botStatus.lastAutoScannerRun?.status === "error" ? "#ef4444" : "#64748b"}
            />
            <Row
              label="Access token until"
              value={
                botStatus.session?.expiresAt
                  ? formatIstDateTime(botStatus.session.expiresAt) + " IST"
                  : "—"
              }
            />
            <button onClick={handleBotToggle} style={{
              width: "100%", marginTop: 16,
              background: botStatus.botActive ? "#2d0a0a" : "#052e16",
              border: `1px solid ${botStatus.botActive ? "#ef4444" : "#22c55e"}`,
              color: botStatus.botActive ? "#ef4444" : "#22c55e",
              padding: "11px", borderRadius: 8, cursor: "pointer",
              fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700,
            }}>
              {botStatus.botActive ? "⏹ Stop Bot" : "▶ Start Bot"}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              style={{
                width: "100%",
                marginTop: 10,
                background: "#0f172a",
                border: "1px solid #334155",
                color: "#94a3b8",
                padding: "8px",
                borderRadius: 8,
                cursor: "pointer",
                fontFamily: "'DM Mono',monospace",
                fontSize: 11,
              }}
            >
              Clear saved token on server
            </button>
            {dashboard?.enabled && (
              <button
                type="button"
                onClick={async () => {
                  setMsg(null);
                  try {
                    await postDashboardLogout();
                    clearBacktestSessionResult();
                    window.location.reload();
                  } catch (err) {
                    setMsg({ type: "error", text: formatApiError(err?.raw || err) });
                  }
                }}
                style={{
                  width: "100%",
                  marginTop: 8,
                  background: "#0f172a",
                  border: "1px solid #334155",
                  color: "#94a3b8",
                  padding: "8px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 11,
                }}
              >
                Sign out of dashboard (login gate)
              </button>
            )}
            <div style={{ marginTop: 12, fontSize: 10, color: "#334155", lineHeight: 1.6 }}>
              The bot runs in the <strong style={{ color: "#64748b" }}>Node.js process</strong>, not in your browser. On AWS EC2 use{" "}
              <code style={{ color: "#f59e0b" }}>pm2 start ecosystem.config.cjs</code> so it keeps running after you close your laptop.
              Upstox access tokens expire around <strong style={{ color: "#64748b" }}>03:30 IST daily</strong> — open Connect again after that.
              {dashboard?.enabled && (
                <>
                  {" "}
                  Dashboard login uses <code style={{ color: "#f59e0b" }}>DASHBOARD_USERNAME</code> /{" "}
                  <code style={{ color: "#f59e0b" }}>DASHBOARD_PASSWORD</code>; set <code style={{ color: "#f59e0b" }}>CORS_ORIGIN</code> to your UI URL
                  (comma-separated if several) so the browser can send the session cookie.
                </>
              )}
            </div>
          </>
        ) : (
          <div style={{ color: "#334155", fontSize: 12, padding: "20px 0" }}>
            Backend not reachable. Start the server with <code style={{ color: "#f59e0b" }}>npm start</code> in the backend folder.
          </div>
        )}
      </div>

      {/* Watchlist */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Syne',sans-serif", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #0f172a" }}>
          Manual Watchlist
        </div>
        <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6, marginBottom: 12 }}>
          Symbols in the active list are always included first. Curated liquid defaults and scanner picks fill the remaining bot subscription slots.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <select
            value={botStatus?.watchlists?.active || "Default"}
            onChange={(e) => handleActiveWatchlist(e.target.value)}
            disabled={watchBusy}
            style={{ flex: 1, minWidth: 160, background: "#020617", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 8, padding: "9px 10px", fontFamily: "'DM Mono',monospace", fontSize: 11 }}
          >
            {(botStatus?.watchlists?.lists || [{ name: "Default", count: 0 }]).map((wl) => (
              <option key={wl.name} value={wl.name}>{wl.name} ({wl.count})</option>
            ))}
          </select>
          <button
            type="button"
            disabled={watchBusy || (botStatus?.watchlists?.active || "Default") === "Default"}
            onClick={() => handleDeleteWatchlist(botStatus?.watchlists?.active)}
            style={{ background: "transparent", border: "1px solid #334155", color: "#94a3b8", borderRadius: 8, padding: "9px 10px", cursor: watchBusy ? "not-allowed" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 10 }}
          >
            Delete
          </button>
        </div>
        <form onSubmit={handleCreateWatchlist} style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <input
            value={newWatchlistName}
            onChange={(e) => setNewWatchlistName(e.target.value)}
            placeholder="New watchlist name"
            disabled={watchBusy}
            style={{ flex: 1, background: "#020617", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 8, padding: "9px 10px", fontFamily: "'DM Mono',monospace", fontSize: 11, outline: "none" }}
          />
          <button
            type="submit"
            disabled={watchBusy || !newWatchlistName.trim()}
            style={{ background: "#0d1f35", border: "1px solid #38bdf8", color: "#38bdf8", padding: "9px 12px", borderRadius: 8, cursor: watchBusy || !newWatchlistName.trim() ? "not-allowed" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 10, fontWeight: 700 }}
          >
            Create
          </button>
        </form>
        <form onSubmit={handleAddWatchlist} style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <input
            value={watchSymbol}
            onChange={(e) => {
              setWatchSymbol(e.target.value.toUpperCase());
              setWatchInstrumentKey("");
            }}
            placeholder="RELIANCE"
            disabled={watchBusy}
            style={{
              flex: 1,
              background: "#020617",
              border: "1px solid #1e293b",
              color: "#e2e8f0",
              borderRadius: 8,
              padding: "10px 12px",
              fontFamily: "'DM Mono',monospace",
              fontSize: 12,
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={watchBusy || !watchSymbol.trim()}
            style={{
              background: "#052e16",
              border: "1px solid #22c55e",
              color: "#22c55e",
              padding: "10px 14px",
              borderRadius: 8,
              cursor: watchBusy || !watchSymbol.trim() ? "not-allowed" : "pointer",
              fontFamily: "'DM Mono',monospace",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            Add
          </button>
        </form>
        {watchSuggestions.length > 0 && (
          <div style={{ marginTop: -8, marginBottom: 14, maxHeight: 150, overflowY: "auto", background: "#020617", border: "1px solid #1e293b", borderRadius: 8 }}>
            {watchSuggestions.slice(0, 20).map((m) => (
              <button
                key={m.instrumentKey || m.symbol}
                type="button"
                onClick={() => {
                  setWatchSymbol(m.symbol);
                  setWatchInstrumentKey(m.instrumentKey || "");
                  setWatchSuggestions([]);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  borderBottom: "1px solid #0a1628",
                  color: "#e2e8f0",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontSize: 11,
                }}
              >
                <strong>{m.symbol}</strong>
                {m.name && <span style={{ color: "#64748b", marginLeft: 8 }}>{m.name}</span>}
              </button>
            ))}
          </div>
        )}
        {(botStatus?.manualWatchlist || []).length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {botStatus.manualWatchlist.map((row) => (
              <div key={row.symbol} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #0a1628" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "#f1f5f9", fontWeight: 700 }}>{row.symbol}</div>
                  <div style={{ fontSize: 9, color: "#475569", marginTop: 2 }}>{row.instrumentKey}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveWatchlist(row.symbol)}
                  disabled={watchBusy}
                  style={{
                    background: "transparent",
                    border: "1px solid #334155",
                    color: "#94a3b8",
                    borderRadius: 6,
                    padding: "5px 8px",
                    cursor: watchBusy ? "not-allowed" : "pointer",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: "#475569", fontSize: 12, padding: "12px 0" }}>No manual symbols yet.</div>
        )}
      </div>

      {/* Active Universe */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Syne',sans-serif", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #0f172a" }}>
          Active Trading Universe
        </div>
        <Row label="Selection" value={botStatus?.scannerSelection || "env"} valueColor="#38bdf8" />
        <Row label="Scanner universe" value={`${botStatus?.trendingUniverseLimit || 0} quote candidates per market scan`} />
        <Row label="Live bot subscription" value={`${botStatus?.watchlist?.length || 0} symbols`} />
        <Row label="Auto trade source" value={botStatus?.autoTradeFromScanner ? "scanner winners + watchlist" : "manual/WebSocket only"} valueColor={botStatus?.autoTradeFromScanner ? "#22c55e" : "#64748b"} />
        <Row
          label="AI entry gate"
          value={botStatus?.advisor?.entryGate?.enabled
            ? `ON - min conf ${botStatus.advisor.entryGate.minConfidence}% - ${botStatus.advisor.entryGate.failClosed ? "fail closed" : "fail open"}`
            : "OFF"}
          valueColor={botStatus?.advisor?.entryGate?.enabled ? "#22c55e" : "#64748b"}
        />
        <Row
          label="AI entry context"
          value={botStatus?.advisor?.entryGate?.enabled
            ? `1d ${botStatus.advisor.entryGate.candleLimits?.daily || 0}, 1h ${botStatus.advisor.entryGate.candleLimits?.hourly || 0}, 15m ${botStatus.advisor.entryGate.candleLimits?.fifteenMinute || 0}, 5m ${botStatus.advisor.entryGate.candleLimits?.fiveMinute || 0}, timeout ${Math.round((botStatus.advisor.entryGate.timeoutMs || 0) / 1000)}s`
            : "disabled"}
        />
        <div style={{ fontSize: 10, color: "#475569", lineHeight: 1.6, marginTop: 8 }}>
          The bot subscribes to a bounded live list for WebSocket ticks. Market Scan can still rank a larger Upstox quote universe on demand.
        </div>
        <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 10, borderTop: "1px solid #0a1628" }}>
          {(botStatus?.watchlist || []).slice(0, 120).map((row) => (
            <div key={row.instrumentKey || row.symbol} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, padding: "8px 0", borderBottom: "1px solid #0a1628" }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 700 }}>{row.symbol}</span>
                <span style={{ fontSize: 10, color: row.source === "manual" ? "#22c55e" : "#38bdf8", marginLeft: 8 }}>{row.source || "scanner"}</span>
              </div>
              <div style={{ fontSize: 10, color: "#64748b", textAlign: "right" }}>
                {row.changePct != null ? `${Number(row.changePct).toFixed(2)}%` : ""}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Strategies */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Syne',sans-serif", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #0f172a" }}>
          🧠 Active Strategies
        </div>
        <div style={{ fontSize: 11, color: "#475569", marginBottom: 14, lineHeight: 1.6 }}>
          Strategies are configured in backend <code style={{ color: "#f59e0b" }}>.env</code>. Toggle using the STRATEGY_* variables and restart.
        </div>
        {[
          ["Momentum (10-bar)", "momentum", "Buys strong uptrends, score ±2"],
          ["MA Crossover", "maCrossover", "SMA20/SMA50 golden/death cross, score ±3"],
          ["Mean Reversion (RSI)", "meanReversion", "RSI <30 buy, >70 sell, score ±3"],
          ["MACD Signal", "macd", "MACD bullish/bearish cross, score ±2"],
          ["Mercury CPR/EMA/VWAP", "mercury", "CPR + EMA10/21 + VWAP breakout, score ±1 to ±8"],
        ].map(([label, key, desc]) => {
          const active = botStatus?.strategies?.[key] !== false;
          return (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #0a1628" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: active ? "#22c55e" : "#334155", boxShadow: active ? "0 0 6px #22c55e" : "none" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: active ? "#e2e8f0" : "#475569", fontWeight: active ? 600 : 400 }}>{label}</div>
                <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>{desc}</div>
              </div>
              <span style={{ fontSize: 10, color: active ? "#22c55e" : "#475569", fontWeight: 700 }}>{active ? "ON" : "OFF"}</span>
            </div>
          );
        })}
        <div style={{ marginTop: 14, padding: 10, background: "#0a1628", borderRadius: 6, fontSize: 10, color: "#475569", lineHeight: 1.6 }}>
          ⚡ Entry threshold: composite score ≥ 4<br />
          All enabled strategy scores are summed before deciding to trade.
        </div>
      </div>

      {/* Environment Config */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #0f172a" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Syne',sans-serif" }}>
            Environment Configuration
          </div>
          <button
            type="button"
            disabled={envConfigRows.length === 0}
            onClick={() => downloadCsv("safe-env-config", envConfigRows)}
            style={{
              background: "#0d1f35",
              border: "1px solid #38bdf8",
              color: envConfigRows.length ? "#38bdf8" : "#475569",
              borderRadius: 7,
              padding: "7px 10px",
              fontFamily: "'DM Mono',monospace",
              fontSize: 10,
              fontWeight: 700,
              cursor: envConfigRows.length ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            Download Env CSV
          </button>
        </div>
        <div style={{ fontSize: 11, color: "#475569", marginBottom: 14, lineHeight: 1.6 }}>
          Safe backend <code style={{ color: "#f59e0b" }}>.env</code> values currently loaded by Node. Credentials and secrets are intentionally hidden.
        </div>
        {configGroups.length > 0 ? configGroups.map(([group, values]) => (
          <div key={group} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: "#38bdf8", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>{group}</div>
            {Object.entries(values).map(([k, v]) => (
              <div key={`${group}-${k}`} style={{ display: "grid", gridTemplateColumns: "minmax(170px, 0.8fr) minmax(0, 1.2fr)", gap: 12, alignItems: "start", padding: "8px 0", borderBottom: "1px solid #0a1628" }}>
                <code style={{ fontSize: 10, color: "#64748b", overflowWrap: "anywhere" }}>{k}</code>
                <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600, overflowWrap: "anywhere", textAlign: "right" }}>{formatConfigValue(v)}</span>
              </div>
            ))}
          </div>
        )) : (
          <div style={{ color: "#475569", fontSize: 12 }}>Backend config not loaded yet.</div>
        )}
        <div style={{ marginTop: 14, padding: 10, background: "#1f0a0a", border: "1px solid #7f1d1d33", borderRadius: 6, fontSize: 10, color: "#fca5a5", lineHeight: 1.7 }}>
          ⚠️ <strong>Before going live:</strong> Set <code>PAPER_TRADING=false</code>, test with small capital, ensure Upstox account has trading API enabled, and verify SEBI compliance.
        </div>
      </div>
    </div>
  );
}
